import type { UiForm, UiTypes } from "various-ui";

export const useComposable = (emit: { (event: "redraw", data: any): void }) => {
    //* 获取I18n
    const i18n = useI18n();

    //* 响应式变量
    const refs = {
        form: ref<InstanceType<typeof UiForm>>(),

        dictionaries: ref<any>({}), //? 字典
        original: ref<any>({}), //? 原始数据

        cPlaceholder: { start: i18n.t("CdsStart"), end: i18n.t("CdsEnd") },
    };

    //* 表单数据
    const data = reactive({
        ref: "",
        alt: "",
        coordinate: {
            "start-steps": 0,
            "end-steps": 0,
            "start": undefined,
            "end": undefined,
        },
    });

    //* 工具函数
    const utils = {
        disposePos: (value: number, next: number) => {
            const n = isNaN(next) ? 0 : Number(next);
            if (refs.dictionaries.value.cds[value - 1]) {
                //* 在CDS字典中存在, 则返回真实坐标 + n
                return refs.dictionaries.value.cds[value - 1].start + n;
            } else if (refs.dictionaries.value.cds.length < value) {
                //* 超出CDS字典长度, 则返回最后一个的真实坐标 + n
                return refs.dictionaries.value.cds.slice(-1)[0].start + n;
            } else {
                //* 否则返回-1
                return -1;
            }
        },
    };

    //* 表单校验规则
    const rules: { [name: string]: UiTypes.verifyRule[] } = {
        coordinate: [
            {
                trigger: "change",
                verify: (data: any) => {
                    const seqLen = refs.dictionaries.value?.sequence?.length || 0;
                    const startSteps = Number(data.coordinate["start-steps"]) || 0;
                    const endSteps = Number(data.coordinate["end-steps"]) || 0;
                    const start = Number(data.coordinate.start) || 0;
                    const end = Number(data.coordinate.end) || 0;

                    const realStart = utils.disposePos(start, startSteps);
                    const realEnd = utils.disposePos(end, endSteps);

                    //* 当start, end不存在或超出范围时
                    if (!realStart || realStart > seqLen) {
                        return {
                            verify: false,
                            message: i18n.t("BioTools.PositionVerify", {
                                type: i18n.t("BioTools.Start"),
                                start: 1,
                                end: refs.dictionaries.value.cds.length,
                            }),
                        };
                    } else if (!realEnd || realEnd > seqLen) {
                        return {
                            verify: false,
                            message: i18n.t("BioTools.PositionVerify", {
                                type: i18n.t("BioTools.End"),
                                start: 1,
                                end: refs.dictionaries.value.cds.length,
                            }),
                        };
                    } else if (realStart > realEnd) {
                        //* 当起始大于结束时
                        return { verify: false, message: i18n.t("Placeholder.StartExceedEnd") };
                    } else if (realEnd - realStart + 1 > 50) {
                        //* 当片段大于20bp时
                        return { verify: false, message: i18n.t("BioTools.MrnaVerify1", { length: 50 }) };
                    } else {
                        return { verify: true, message: "" };
                    }
                },
            },
        ],
        ref: [
            {
                trigger: "verify",
                verify: (data: any) => {
                    if (data.ref) return { verify: true, message: "" };
                    else {
                        return { verify: false, message: "Please provide the coordinate" };
                    }
                },
            },
        ],
        alt: [
            {
                trigger: "verify",
                verify: (data: any) => {
                    if (data.alt && !/^([A|T|C|G]*)$/.test(data.alt)) {
                        return { verify: false, message: "only alphabet 'ATCG' are allowed" };
                    } else {
                        return { verify: true, message: "" };
                    }
                },
            },
        ],
    };

    //* 函数列表
    const methods = {
        //* 初始化函数
        init: function (origin: any, dictionaries: any) {
            //* 重置表单
            refs.form.value?.reset();

            //* 数据初始化
            data.ref = "";
            data.alt = "";
            data.coordinate.end = undefined;
            data.coordinate.start = undefined;
            data.coordinate["end-steps"] = 0;
            data.coordinate["start-steps"] = 0;

            //* 数据缓存
            refs.original.value = origin || {};
            refs.dictionaries.value = dictionaries || {};
        },

        //* submit事件
        submit: function (callBack: (data: any) => void) {
            const startSteps = Number(data.coordinate["start-steps"]) || 0;
            const start = Number(data.coordinate.start) || 0;
            refs.form.value?.validator((valid: boolean) => {
                valid && callBack && callBack({ alt: data.alt, ref: data.ref, gene_pos: utils.disposePos(start, startSteps) });
            });
        },

        //* 切换Alteration
        changeAlt: function () {
            const startSteps = Number(data.coordinate["start-steps"]) || 0;
            const endSteps = Number(data.coordinate["end-steps"]) || 0;
            const start = Number(data.coordinate.start) || 0;
            const end = Number(data.coordinate.end) || 0;

            const realStart = utils.disposePos(start, startSteps);
            const realEnd = utils.disposePos(end, endSteps);

            const hanlder = (valid: boolean) => {
                if (valid) {
                    emit("redraw", { ref: data.ref, alt: data.alt, end: realEnd, start: realStart });
                }
            };

            data.alt = data.alt.toUpperCase();
            if (realStart && realEnd) {
                refs.form.value?.validator(hanlder, ["coordinate", "alt"]);
            } else {
                refs.form.value?.validator(() => {}, ["alt"]);
            }
        },

        //* 切换预测位置
        changeCoordinate: function () {
            const hanlder = (valid: boolean) => {
                if (valid) {
                    const startSteps = Number(data.coordinate["start-steps"]) || 0;
                    const endSteps = Number(data.coordinate["end-steps"]) || 0;
                    const start = Number(data.coordinate.start) || 0;
                    const end = Number(data.coordinate.end) || 0;

                    const realStart = utils.disposePos(start, startSteps);
                    const realEnd = utils.disposePos(end, endSteps);
                    const ref = [];

                    for (let i = realStart; i <= realEnd; i++) {
                        ref.push(refs.dictionaries.value.sequence.charAt(i - 1));
                    }

                    data.ref = ref.join("");
                    const renderData = { start: realStart, end: realEnd, ref: data.ref, alt: "" };
                    if (data.alt && /^([A|T|C|G]*)$/.test(data.alt)) {
                        renderData.alt = data.alt;
                    }

                    emit("redraw", renderData);
                } else {
                    data.ref = "";
                }
            };

            refs.form.value?.validator(hanlder, ["coordinate"]);
        },
    };

    return { refs, data, rules, methods };
};
